import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { BUSINESS, BuyRoadsideDetails, PERSON } from '@/interfaces/pages/buy-roadside.interface';
import { IFoundVehicle, IReviewSummary, IReviewSummaryItem, RACQAddressResult } from '@/interfaces';
import { useFindCarStore } from '../find-car/find-car.state';
import useVehicleEligibility from '@/composables/useVehicleEligibility';
import usePartyMatchState from '@/composables/usePartyMatchState';
import { dateToDateString } from '@/utils/data-transforms';
import { BuyRoadsideService as _buyRoadsideService } from '@/services/store/buy-roadside.service';
import { RoadsideAssistanceService as _roadsideAssistanceService } from '@/services/store/roadside-assistance.service';
import { InsuranceQuoteService as _iq } from '@/services/store/insurance-quote.service';
import { Product } from '@/interfaces/entities/product.interface';
import { useAccountStore } from '../account/account.state';
import {
  mapToCreatePartyRequestForBusiness,
  mapToCreatePartyRequestForPersonal,
  mapToCreateQuoteRequest,
} from '@/mappers/roadside-assistance-mapper';
import { CreateQuoteResponse } from '@/interfaces/entities/create-quote.interface';
import { PartyMatchStatus } from '@/interfaces/entities/roadside-assistance.interface';
import { FinaliseQuoteResponse } from '@/interfaces/entities/finalise-quote.interface';
import { CreatePartyRequest } from '@/interfaces/entities/create-party.interface';
import { GenericInvoiceEmailRequest } from '@/interfaces/entities/generic-invoice-email-request.interface';
import { FormEvents } from '@/enums/form-events.enum';
import {
  IAnalyticsForm,
  IAnalyticsPaymentDetail,
  IAnalyticsProfileInfo,
  IFormAnalytics,
} from '@/interfaces/forms/analytics-interface';
import { useAnalyticsStore } from '../analytics/analytics.state';
import { useFormStore } from '../form/formStore';
import { calculateAge } from '@/utils/common-helper';
import { RACQ } from '@/constants/general-words.const';
import {
  getRoadsideProductCodeFromProductName,
  getRoadsideProductNameFromProductCode,
  RSA_ARV002,
  RSA_RACQ_RV,
} from '@/utils/roadside-helpers';
import { CardTypes } from '@/enums/payment-types.enum';
import { useJeopardyStore } from '../jeopardy/jeopardy.state';
import { JP_RSA_GENERIC } from '@/constants/jeopardy-codes.const';
import { IVehicleEligibility } from '@/interfaces/entities/vehicle-eligibility.interface';

export const useBuyRoadsideStore = defineStore('BuyRoadside', () => {
  const accountStore = useAccountStore();
  const analyticsStore = useAnalyticsStore();
  const formStore = useFormStore();
  const findCarStore = useFindCarStore();
  const jpStore = useJeopardyStore();

  // State
  const buyRoadsideDetails = ref<BuyRoadsideDetails>({
    vehicle: undefined,
    personalDetails: undefined,
    businessDetails: undefined,
    selectedProductCode: '',
    immediateAssistance: null,
    roadworthyCondition: null,
  });
  const creditCardDetails = ref<Record<string, string>>({});
  const paymentReceipt = ref<{
    result: string;
    transactionId: string;
    reference: string;
  }>();
  const apttusResponse = ref<CreateQuoteResponse | null>(null); // Response from the createQuote call
  const finaliseQuoteQuoteId = ref<string | null>(null); // The last QuoteId that was finalised
  const finaliseQuoteResponse = ref<FinaliseQuoteResponse | null>(null); // Response from the finaliseQuote call
  const preselectedProduct = ref(''); // querystring value for the preselected product if passed
  const validFor = ref(PERSON); // querystring value of the validFor if passed (Business/Person)
  const reviewSummaryEdit = ref<IReviewSummary>(); // The reviewSummary section that was clicked
  const reviewSummaryItemEdit = ref<IReviewSummaryItem>(); // The reviewSummaryItem that was clicked
  const roadsideProducts = ref<Product[]>([]); // The list of roadside products
  const isLoggedIn = ref(false); // Whether the current user has a current session in sitecore
  const apttusId = ref<string | null>(null); // apttusId from CreateParty call or from the logged in user
  const d365Id = ref<string | null>(null); // d365Id from CreateParty call or from the logged in user
  const isPartyMatched = ref(false); // Whether there is a party match for the entered details. If the user has been created in the session this will be false
  const isPartyCreated = ref(false); // Whether a party has been created from the entered details
  const isQuoteCreated = ref(false); // Whether a quote has been created from the entered details
  const isQuoteFinalised = ref(false); // Whether a quote has been finalised from the entered details
  const isBusiness = computed(() => validFor.value?.toLowerCase() === BUSINESS.toLowerCase()); // Whether the current flow is for business
  const isDirectDebit = ref(false); // Whether the payment is direct debit
  const selectedProductCode = computed(() => buyRoadsideDetails.value.selectedProductCode); // Tracks the selected product - used to invalidate the quote on product selection change
  const paymentAttemptCount = ref(0); // Tracks the number of attempts made to pay for the quote
  const finaliseQuoteAttemptCount = ref(0); // Tracks the number of attempts made to finalise the quote
  const createQuoteAttemptCount = ref(0); // Tracks the number of attempts to create a quote
  const selectedProductName = computed(
    () =>
      buyRoadsideDetails.value.selectedProductCode &&
      getRoadsideProductNameFromProductCode(buyRoadsideDetails.value.selectedProductCode)
  );
  const selectedProduct = computed(() =>
    roadsideProducts.value.find((x) => x.productCode === selectedProductCode.value)
  );
  const preSelectedProductCode = computed(() => getRoadsideProductCodeFromProductName(preselectedProduct.value));
  const preSelectedProduct = computed(() =>
    roadsideProducts.value.find((x) => x.productCode === preSelectedProductCode.value)
  );
  const isProductPreselected = computed(() => preSelectedProductCode.value !== undefined);
  const isPreselectedProductRV = computed(() => preSelectedProduct.value?.productName === RSA_RACQ_RV);
  const checkIsEligibleForRVOnly = (business: boolean, eligibility?: IVehicleEligibility) => {
    return !business && eligibility?.isEligible && eligibility?.productName === RSA_RACQ_RV;
  };
  const isEligibleForRVOnly = computed(() =>
    checkIsEligibleForRVOnly(isBusiness.value, vehicleEligibility.value as IVehicleEligibility)
  );
  const isVehicleEligibleForRV = computed(
    () => preSelectedProduct.value?.productName === RSA_RACQ_RV && isEligibleForRVOnly.value
  );
  const isVehicleEligibleForOtherProducts = computed(
    () => preSelectedProduct.value?.productName !== RSA_RACQ_RV && !isEligibleForRVOnly.value
  );
  const isPreselectedProductEligibleForVehicle = computed(
    () => isVehicleEligibleForRV.value || isVehicleEligibleForOtherProducts.value
  );

  const hasExistingRoadsideProduct = ref(false); // Tracks whether there is an existing roadside product for the user
  const updatePostalAddress = ref(false); // Tracks whether a party matched user has the same postal address as we have stored in connect
  const isMissingResidentialAddress = ref(false); //Protecht 1149549 DSQ-2173 D365 missing residential address

  // Define Composables
  // ------------------------------------------------------------- //
  const { vehicleEligibility, setVehicleEligibility } = useVehicleEligibility();
  const { partyMatchState, setPartyMatchState } = usePartyMatchState();

  // Define Functions
  // ------------------------------------------------------------- //
  watch(selectedProductCode, (newVal) => {
    if (newVal) {
      invalidateQuote();
      createQuoteAttemptCount.value = 0;
      finaliseQuoteAttemptCount.value = 0;
    }
  });

  watch(preselectedProduct, (newVal, oldVal) => {
    // Select the product when the preselected product changes (from querystring)
    buyRoadsideDetails.value.selectedProductCode = preSelectedProductCode.value ?? '';
  });

  watch(vehicleEligibility, (newVal, oldVal) => {
    if (
      newVal === oldVal ||
      !buyRoadsideDetails.value.selectedProductCode ||
      buyRoadsideDetails.value.selectedProductCode === ''
    )
      return;
    // ensure that the selected product is still valid for the vehicle eligibility. If not, clear the selected product
    const newValIsEligibleForRVOnly = checkIsEligibleForRVOnly(isBusiness.value, newVal as IVehicleEligibility);
    if (
      (newValIsEligibleForRVOnly && buyRoadsideDetails.value.selectedProductCode !== RSA_ARV002) ||
      (!newValIsEligibleForRVOnly && buyRoadsideDetails.value.selectedProductCode === RSA_ARV002)
    ) {
      buyRoadsideDetails.value.selectedProductCode = '';
    }
  });

  watch(validFor, async (newVal, oldVal) => {
    if (newVal === oldVal) return;
    await initiatePopulateRoadsideProducts();
  });

  async function initiatePopulateRoadsideProducts() {
    // Populate the roadside products when the validFor changes (Business/Person)
    try {
      await populateRoadsideProducts();
    } catch (e) {
      console.error('failed to retrieve products', e);
      await jpStore.showJeopardyPanel({
        jpCode: JP_RSA_GENERIC,
      });
    }
  }

  initiatePopulateRoadsideProducts();

  function setVehicleDetails(vehicle: IFoundVehicle, vehicleColour: string) {
    const findCarStore = useFindCarStore();
    if (!vehicle) {
      return;
    }

    // If the vehicle is manually selected then the registration needs to be stored as the response from motorweb will be empty
    let regoNumber = vehicle.registrationNumber;
    const isFoundByRegoLookup = regoNumber !== '';
    if (!regoNumber) {
      regoNumber = findCarStore.registrationNumber;
    }

    buyRoadsideDetails.value.vehicle = {
      isFoundByRegoLookup: isFoundByRegoLookup,
      vehicleYear: vehicle.manufacturingYear.toString(),
      vehicleRegistration: regoNumber,
      vehicleBody: vehicle.body.description,
      vehicleModel: vehicle.model.description,
      vehicleMake: vehicle.make.description,
      vehicleColour: vehicleColour, // vehicleColour is provided by the user explicitly, not using motorweb for BuyRoadside journey
      vehicleVariant: vehicle.variant.description,
      vehicleGlassesId: vehicle.glassesId,
    };
  }

  function clearVehicleDetails() {
    buyRoadsideDetails.value.vehicle = undefined;
  }

  /**
   * Used to track which review summary edit link was clicked
   */
  function setReviewSummaryEditClicked(summary: IReviewSummary, summaryReviewItem: IReviewSummaryItem) {
    reviewSummaryEdit.value = summary;
    reviewSummaryItemEdit.value = summaryReviewItem;
  }

  function mapPaymentDetails(formData: Record<string, any>) {
    creditCardDetails.value = {
      cardToken: formData.cardToken,
      cardHolder: formData.cardHolder,
      maskedCardNumber: formData.MaskedCardNumber,
      cardExpiry: formData.cardExpiry,
      cardType: formData.cardType,
      autoRenew: formData.autoRenew,
    };

    // Set direct debit state
    if (formData.autoRenew?.toLowerCase() === 'true') {
      isDirectDebit.value = true;
    }
  }

  async function setPartyMatchStateBuyRoadside(): Promise<boolean> {
    const response = await setPartyMatchState(
      buyRoadsideDetails.value?.personalDetails!.firstName,
      buyRoadsideDetails.value?.personalDetails!.lastName,
      dateToDateString(buyRoadsideDetails.value?.personalDetails!.birthDate, 'yyyy-MM-dd'),
      buyRoadsideDetails.value?.personalDetails!.emailAddress,
      buyRoadsideDetails.value?.personalDetails!.phoneNumber,
      buyRoadsideDetails.value?.personalDetails!.postalAddress?.dpid
    );

    // Set store values to response, otherwise null/false
    d365Id.value = response && partyMatchState.value?.d365Id ? partyMatchState.value.d365Id : null;
    apttusId.value = response && partyMatchState.value?.apttusId ? partyMatchState.value.apttusId : null;
    isPartyMatched.value = response && partyMatchState.value?.matchStatus === PartyMatchStatus.Exact;

    if (isPartyMatched.value && buyRoadsideDetails.value.personalDetails?.postalAddress?.dpid !== undefined) {
      await checkUpdatePostalAddressRequired(
        d365Id.value!,
        buyRoadsideDetails.value.personalDetails.postalAddress.dpid
      );
    }

    // Check if residential address is missing
    if (isPartyMatched.value) await checkIsMissingResidentialAddress(d365Id.value!);
    else isMissingResidentialAddress.value = false;

    return response;
  }

  function pushAnalytics(options: {
    event: FormEvents;
    form: IAnalyticsForm;
    profileInfo?: IAnalyticsProfileInfo;
    payment?: Partial<IAnalyticsPaymentDetail>;
    omitProductName?: boolean;
    omitDetail?: boolean;
  }) {
    const model: IFormAnalytics = {
      event: options.event,
      form: options.form,
    };

    if (!options.form.productName && selectedProductName.value?.length && !options.omitProductName) {
      options.form.productName = selectedProductName.value;
    }

    if (!options.form.detail && findCarStore.selectedVehicle && !options.omitDetail) {
      options.form.detail = {
        bodyType: findCarStore.selectedVehicle.body.code,
        fuelType: findCarStore.selectedVehicle.fuelType.code,
        makeName: findCarStore.selectedVehicle.make.description,
        modelName: findCarStore.selectedVehicle.model.description,
        vehicleValue: findCarStore.selectedVehicle.marketValue.toString(),
        vehicleYear: findCarStore.selectedVehicle.manufacturingYear.toString(),
      };
    }

    // Set ProfileInfo
    model.profileInfo = {
      ...(d365Id.value && { contactID: d365Id.value }),
    } as IAnalyticsProfileInfo;

    if (options.payment) {
      model.payment = options.payment;
    }
    model.form.name = formStore.formName;

    analyticsStore.pushAnalytics(model);
  }

  async function populateRoadsideProducts() {
    const products = await _buyRoadsideService.getRoadsideProducts(validFor.value || PERSON);
    if (products === null) {
      roadsideProducts.value = [];
      return;
    }
    roadsideProducts.value = products;
  }

  function getProductPrice(productCode: string): number {
    return roadsideProducts.value.find((x) => x.productCode === productCode)?.price ?? 0;
  }

  function invalidatePartyCreated() {
    apttusId.value = null;
    d365Id.value = null;
    isPartyCreated.value = false;
    invalidateQuote();
  }

  async function createParty(): Promise<void> {
    if (isPartyMatched.value || isPartyCreated.value || isLoggedIn.value) {
      // If the details entered result in a party match, a party has already been created from the details or the user is already logged in then we don't want to create a new party
      return;
    }

    let createPartyRequest: CreatePartyRequest;

    if (isBusiness.value) {
      createPartyRequest = mapToCreatePartyRequestForBusiness(
        buyRoadsideDetails.value.businessDetails!.abn,
        buyRoadsideDetails.value.businessDetails!.acn ?? null,
        buyRoadsideDetails.value.businessDetails!.businessName,
        buyRoadsideDetails.value.businessDetails!.emailAddress,
        buyRoadsideDetails.value.businessDetails!.phoneNumber,
        getRoadsideProductNameFromProductCode(buyRoadsideDetails.value.selectedProductCode!) ?? '',
        buyRoadsideDetails.value.businessDetails!.homeAddress as RACQAddressResult,
        buyRoadsideDetails.value.businessDetails!.postalAddress as RACQAddressResult
      );
    } else {
      createPartyRequest = mapToCreatePartyRequestForPersonal(
        buyRoadsideDetails.value.personalDetails!.title,
        buyRoadsideDetails.value.personalDetails!.firstName,
        buyRoadsideDetails.value.personalDetails!.lastName,
        dateToDateString(buyRoadsideDetails.value.personalDetails!.birthDate, 'yyyy-MM-dd'),
        buyRoadsideDetails.value.personalDetails!.emailAddress,
        buyRoadsideDetails.value.personalDetails!.phoneNumber,
        getRoadsideProductNameFromProductCode(buyRoadsideDetails.value.selectedProductCode!) ?? '',
        buyRoadsideDetails.value.personalDetails!.homeAddress as RACQAddressResult,
        buyRoadsideDetails.value.personalDetails!.postalAddress as RACQAddressResult
      );
    }

    const response = await _roadsideAssistanceService.createParty(createPartyRequest);

    if (response) {
      apttusId.value = response.apttusId;
      d365Id.value = response.d365Id;
      isPartyCreated.value = true;
    }
  }

  async function checkUpdatePostalAddressRequired(d365Id: string, dpid: string) {
    try {
      const res = await _iq.checkUpdatePostalAddressRequired(d365Id, dpid);
      updatePostalAddress.value = res ?? false;
    } catch {
      //do nothing
    }
  }

  async function checkIsMissingResidentialAddress(d365Id: string) {
    try {
      const res = await _roadsideAssistanceService.checkIsMissingResidentialAddress(d365Id);
      isMissingResidentialAddress.value = res ?? false;
    } catch {
      isMissingResidentialAddress.value = false;
    }
  }

  //#region Quote
  async function createQuote(): Promise<void> {
    const maxCreateQuoteAttempts = 2;
    if (
      !apttusId.value || // need an apttusId to continue
      (!isLoggedIn.value && !isPartyCreated.value && !isPartyMatched.value) || // need a party to create the quote for
      isQuoteCreated.value || // don't create duplicate quotes
      createQuoteAttemptCount.value >= maxCreateQuoteAttempts // retry limit hit
    ) {
      // Don't create a quote if there's no apttusId or we have no party details or we've already created one
      return;
    }

    createQuoteAttemptCount.value++;
    const vehicleDetail = vehicleEligibility.value?.vehicleDetail;
    let productName = getRoadsideProductNameFromProductCode(buyRoadsideDetails.value.selectedProductCode!) ?? '';

    if (isBusiness.value) {
      // Remove 'RACQ' prefix from productName
      productName = productName.replaceAll(`${RACQ} `, '');
    }

    const postalAddressState = isBusiness.value
      ? buyRoadsideDetails.value.businessDetails!.postalAddress?.state
      : buyRoadsideDetails.value.personalDetails!.postalAddress?.state;

    const createQuoteRequest = mapToCreateQuoteRequest(
      vehicleDetail,
      buyRoadsideDetails.value.selectedProductCode ?? '',
      productName,
      apttusId.value ?? '',
      vehicleDetail?.registrationNumber ?? buyRoadsideDetails.value.vehicle?.vehicleRegistration ?? '',
      vehicleDetail?.manufacturingYear ?? Number(buyRoadsideDetails.value.vehicle?.vehicleYear),
      vehicleDetail?.stateCode ?? postalAddressState,
      vehicleDetail?.body.colour ?? buyRoadsideDetails.value.vehicle?.vehicleColour ?? '',
      vehicleDetail?.make.description ?? buyRoadsideDetails.value.vehicle?.vehicleMake ?? '',
      vehicleDetail?.model.description ?? buyRoadsideDetails.value.vehicle?.vehicleModel ?? '',
      null
    );

    const response = await _roadsideAssistanceService.createQuote(createQuoteRequest);
    if (response === null) {
      // Call failed
      invalidateQuote();
    } else if (response.isError || !isPriceValid(response)) {
      // Quote has an error or the price is invalid
      invalidateQuote();
      await createQuote(); // retry
    } else {
      // Success
      apttusResponse.value = response;
      createQuoteAttemptCount.value = 0;
      isQuoteCreated.value = true;
    }
  }

  function invalidateQuote() {
    // Clear the fields related to creating a quote and downstream attempt counts
    apttusResponse.value = null;
    isQuoteCreated.value = false;
    finaliseQuoteAttemptCount.value = 0;
    paymentAttemptCount.value = 0;
  }

  function invalidateQuoteFinalising() {
    // Clear the fields related to a finalised quote
    finaliseQuoteResponse.value = null;
    isQuoteFinalised.value = false;
  }

  function checkQuoteFinalisation(): boolean {
    if (isQuoteFinalised.value && finaliseQuoteQuoteId.value != apttusResponse.value?.apttusQuoteId) {
      // The quote has changed, so it requires finalising again.
      invalidateQuoteFinalising();
    }
    return isQuoteFinalised.value;
  }

  async function finaliseQuote(): Promise<boolean> {
    try {
      if (!apttusResponse.value?.apttusCartId || !apttusResponse.value.apttusQuoteId) {
        throw new Error('MISSING_PARAMS');
      }

      finaliseQuoteQuoteId.value = apttusResponse.value.apttusQuoteId ?? '';

      const response: FinaliseQuoteResponse | null = await _roadsideAssistanceService.finaliseQuote(
        apttusResponse.value.apttusQuoteId ?? '',
        apttusResponse.value?.apttusCartId ?? ''
      );
      if (!response || response.isError) {
        // Failure
        invalidateQuoteFinalising();
        throw new Error('API_FAILED');
      }

      if (!response.isError) {
        // Success
        finaliseQuoteResponse.value = response;
        isQuoteFinalised.value = true;
        return true;
      }

      throw new Error('MISSING_PARAMS');
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async function submitPurchase() {
    try {
      let result: any;

      if ([CardTypes.GooglePay, CardTypes.ApplePay].includes(creditCardDetails.value.cardType as CardTypes)) {
        const response = await _roadsideAssistanceService.submitPurchase({
          walletToken: creditCardDetails.value.cardToken,
          walletProvider: creditCardDetails.value.cardType,
          // Common fields
          prn: finaliseQuoteResponse.value?.prn,
          amount: apttusResponse.value?.price ?? 0,
          invoiceId: finaliseQuoteResponse.value?.invoiceLineItem?.invoiceId ?? null,
          apttusId: apttusId.value ?? null,
          quoteId: apttusResponse.value?.apttusQuoteId ?? '',
        });

        // Existing shape expected by callers
        result = response;
      } else {
        // Existing credit card path
        result = await _roadsideAssistanceService.submitPurchase({
          // Standard Fields
          cardToken: creditCardDetails.value.cardToken,
          autoRenew: creditCardDetails.value.autoRenew,
          prn: finaliseQuoteResponse.value?.prn,
          amount: apttusResponse.value?.price,
          invoiceId: finaliseQuoteResponse.value?.invoiceLineItem.invoiceId,
          apttusId: apttusId.value,
          // AutoRenew Exclusive Fields
          cardHolder: creditCardDetails.value.cardHolder,
          MaskedCardNumber: creditCardDetails.value.maskedCardNumber,
          cardExpiry: creditCardDetails.value.cardExpiry,
          cardType: creditCardDetails.value.cardType,
          assetId: finaliseQuoteResponse.value?.assetId,
        });
      }

      paymentReceipt.value = result;
      return result;
    } catch {
      return null;
    }
  }

  async function sendEmail(toAddress: string) {
    const firstName = isBusiness.value
      ? buyRoadsideDetails.value.businessDetails?.businessName
      : buyRoadsideDetails.value.personalDetails?.firstName;

    const phoneNumber = isBusiness.value
      ? buyRoadsideDetails.value.businessDetails?.phoneNumber
      : buyRoadsideDetails.value.personalDetails?.phoneNumber;

    const emailAddress = isBusiness.value
      ? buyRoadsideDetails.value.businessDetails?.emailAddress
      : buyRoadsideDetails.value.personalDetails?.emailAddress;

    const make = buyRoadsideDetails.value.vehicle?.vehicleMake;
    const model = buyRoadsideDetails.value.vehicle?.vehicleModel;
    const year = buyRoadsideDetails.value.vehicle?.vehicleYear;
    const variant = findCarStore.selectedVehicle?.variant?.description ?? '';
    const body = findCarStore.selectedVehicle?.body?.description ?? '';
    const colour = buyRoadsideDetails.value.vehicle?.vehicleColour ?? '';

    const vehicleStringForEmail = `<p><strong>${make} ${model} ${year}</strong></p><p>${body} ${variant} ${colour}</p>`;

    const emailRequest: GenericInvoiceEmailRequest = {
      receiptNumber: finaliseQuoteResponse.value?.prn,
      amount: apttusResponse.value!.price!,
      product: getRoadsideProductNameFromProductCode(buyRoadsideDetails.value.selectedProductCode!)! + '*',
      emailAddress,
      firstName,
      paymentMethod: creditCardDetails.value.cardType,
      phoneNumber,
      vehicle: vehicleStringForEmail,
      d365Id: d365Id.value ?? undefined,
      toAddress: toAddress,
      isJoinOnRoad: false,
    };

    await _roadsideAssistanceService.sendGenericInvoiceEmail(emailRequest);
  }

  async function hasExistingPolicyForRego(): Promise<boolean | null> {
    const vehicleRego = findCarStore.registrationNumber;
    if (!apttusId.value || !vehicleRego) {
      return null;
    }
    return _roadsideAssistanceService.hasExistingPolicyForRego(apttusId.value, vehicleRego);
  }

  function isPriceValid(response: CreateQuoteResponse) {
    if (!response.price || response.price === 0 || response.price < 0) {
      return false;
    }
    return true;
  }
  //#endregion

  const isUnderAge = (birthDate: Date): boolean | null => {
    try {
      const minAge = 16;
      return calculateAge(birthDate) < minAge;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  async function setDetailsFromIdentity(): Promise<void> {
    // Get identity details and prefill user details
    await accountStore.fetchIdentityDetails();
    const identity = accountStore.identityDetails;

    // Fill in user details from identity
    if (identity !== undefined) {
      if (!buyRoadsideDetails.value.personalDetails) {
        buyRoadsideDetails.value.personalDetails = {
          title: '',
          firstName: '',
          lastName: '',
          birthDate: new Date(),
          checkPostalAddress: false,
          emailAddress: '',
          phoneNumber: '',
          homeAddress: undefined,
          postalAddress: undefined,
        };
      }
      const personalDetails = buyRoadsideDetails.value.personalDetails;

      if (identity.birthDate) personalDetails.birthDate = identity.birthDate!;
      if (identity.email) personalDetails.emailAddress = identity.email!;
      if (identity.firstName) personalDetails.firstName = identity.firstName!;
      if (identity.lastName) personalDetails.lastName = identity.lastName!;
      if (identity.d365Id) d365Id.value = identity.d365Id!;
      if (identity.apttusId) apttusId.value = identity.apttusId!;
      if (identity.phoneNumber) personalDetails.phoneNumber = identity.phoneNumber!;
      if (identity.hasRoadsideProduct) hasExistingRoadsideProduct.value = identity.hasRoadsideProduct!;
    }

    isLoggedIn.value = !!identity?.d365Id;
  }

  function getPriceFromRoadsideProducts(roadsideProductCode: string): number | undefined {
    if (roadsideProducts.value.length === 0) {
      return undefined;
    }
    const product = roadsideProducts.value.find((x: Product) => x.productCode === roadsideProductCode);
    return product?.price ?? undefined;
  }

  return {
    buyRoadsideDetails,
    creditCardDetails,
    paymentReceipt,
    apttusResponse,
    preselectedProduct,
    validFor,
    reviewSummaryEdit,
    reviewSummaryItemEdit,
    vehicleEligibility,
    partyMatchState,
    roadsideProducts,
    isPartyCreated,
    isPartyMatched,
    hasExistingRoadsideProduct,
    d365Id,
    apttusId,
    isLoggedIn,
    isQuoteFinalised,
    finaliseQuoteResponse,
    isBusiness,
    isQuoteCreated,
    isDirectDebit,
    paymentAttemptCount,
    finaliseQuoteAttemptCount,
    updatePostalAddress,
    isMissingResidentialAddress,
    setVehicleEligibility,
    setVehicleDetails,
    clearVehicleDetails,
    mapPaymentDetails,
    setReviewSummaryEditClicked,
    setPartyMatchStateBuyRoadside,
    populateRoadsideProducts,
    getProductPrice,
    checkUpdatePostalAddressRequired,
    setDetailsFromIdentity,
    createParty,
    createQuote,
    getPriceFromRoadsideProducts,
    hasExistingPolicyForRego,
    checkQuoteFinalisation,
    finaliseQuote,
    submitPurchase,
    sendEmail,
    isUnderAge,
    invalidatePartyCreated,
    pushAnalytics,
    selectedProduct,
    preSelectedProductCode,
    preSelectedProduct,
    isProductPreselected,
    isPreselectedProductEligibleForVehicle,
    isEligibleForRVOnly,
    isPreselectedProductRV,
  };
});
